import type { ButtonHTMLAttributes, ReactNode } from 'react';
import './botao.css';

export type VarianteBotao = 'primario' | 'secundario' | 'fantasma' | 'perigo';

/**
 * Desabilitado exige motivo — pelo tipo, não pela boa vontade de quem escreve.
 *
 * Botão apagado sem explicação gera chamado no suporte. O motivo vira `title`
 * e, quando não há texto visível, também o `aria-label`.
 */
/*
 * Desabilitado SEMPRE com motivo — mas aceitando booleano.
 *
 * A primeira versão exigia `desabilitado: true` literal. Na adoção, quinze dos
 * dezenove usos passam uma expressão (`disabled={enviando}`), e um literal
 * obrigaria a espalhar `{...(cond ? { desabilitado: true, motivo } : {})}` em
 * cada um. O que a regra protege é o motivo, não o literal: se a prop pode ser
 * verdadeira, o motivo é obrigatório. Sem a prop, sem motivo.
 */
type Travado = { desabilitado: boolean; motivo: string } | { desabilitado?: never; motivo?: never };

/*
 * `title` fica permitido. Ele não é só o motivo do desabilitado: o botão de
 * encerrar a conversa, habilitado, usa o `title` para prometer que dá para
 * reabrir — e é essa promessa que justifica não haver confirmação. Quando há
 * motivo, o motivo vence.
 */
type Base = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'disabled' | 'children'>;

export type BotaoProps = Base &
  Travado & {
    variante?: VarianteBotao;
    /** Compacto (26px), para barra de ferramentas e linha de tabela. */
    miudo?: boolean;
    /** Quadrado, sem texto. Exige `rotulo`. */
    icone?: boolean;
    /** Rótulo para leitor de tela quando não há texto visível. */
    rotulo?: string;
    children?: ReactNode;
  };

export function Botao({
  variante = 'secundario',
  miudo = false,
  icone = false,
  rotulo,
  desabilitado = false,
  motivo,
  title,
  children,
  ...resto
}: BotaoProps) {
  const classe = ['bt', `bt--${variante}`, miudo ? 'bt--miudo' : null, icone ? 'bt--icone' : null]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classe}
      disabled={desabilitado}
      title={(desabilitado ? motivo : undefined) ?? title ?? rotulo}
      aria-label={rotulo}
      {...resto}
    >
      {children}
    </button>
  );
}
